export type L2CPeriodGrain = 'day' | 'week' | 'month' | 'year';

export type L2CComparisonPreset =
  | 'latest_vs_prior'
  | 'prior_vs_before'
  | 'same_period_yoy'
  | 'ytd_vs_prior_ytd'
  | 'custom';

export type L2CComparisonMode = 'wow' | 'mom' | 'yoy' | 'ytd' | 'dod' | 'custom';

export type L2CNarrativeSource = 'template' | 'llm' | null;

export type L2CStatus = 'green' | 'yellow' | 'red';

export type L2CMetricFormat = 'number' | 'currency' | 'percent' | 'ratio';

/** Default page size for stage breakdown mover tables. */
export const L2C_MOVERS_PAGE_SIZE = 10;

export interface L2CMetric {
  key: string;
  label: string;
  value: number | null;
  prior_value: number | null;
  projected_value?: number | null;
  projection_label?: string | null;
  wow_pct: number | null;
  direction: 'up' | 'down' | 'flat';
  sentiment: 'good' | 'bad' | 'neutral';
  status: L2CStatus;
  maturation_sensitive?: boolean;
  maturation_note?: string | null;
  status_reason?: string | null;
  definition_note?: string | null;
  format: L2CMetricFormat;
}

export interface L2CMover {
  dimension: string;
  this_period: number;
  prior_period: number;
  change: number;
  change_pct: number;
  status: string;
}

export interface L2CDriver {
  id: string;
  label: string;
  default?: boolean;
}

export interface L2CPanel {
  what_happened: string;
  why: string;
  suggestion: string;
  movers: L2CMover[];
  method_note: string;
  breakdown_unavailable?: boolean;
  drivers?: L2CDriver[];
  active_driver?: string | null;
  lens_hint?: string | null;
  breakdown_total_count?: number;
  breakdown_page?: number;
  breakdown_page_size?: number;
  /** Full mover lists per driver — used for client-side pagination. */
  breakdowns?: Record<string, { movers: L2CMover[]; total_count: number }>;
  /** Cached dimension breakdowns for supporting-metric drill-down (key: `{metric_key}:{driver_id}`). */
  metric_breakdowns?: Record<string, { movers: L2CMover[]; total_count?: number }>;
  /** Cached "Metric Summary" plain-language blurb per metric key, driver-agnostic. */
  metric_narratives?: Record<string, { summary: string; source?: string | null }>;
  narrative_source?: string | null;
  /** True while LLM executive summary is generating in the background. */
  narrative_pending?: boolean;
}

export interface L2CStage {
  id: string;
  name: string;
  order: number;
  status: L2CStatus;
  data_unavailable?: boolean;
  headline_metrics: L2CMetric[];
  supporting_metrics?: L2CMetric[];
  primary_metric_key?: string | null;
  headline_metric_keys?: string[];
  supporting_metric_keys?: string[];
  /** Metrics spotlighted on the chain phase row. */
  card_metric_keys?: string[];
  collapsed?: boolean;
  emphasized?: boolean;
  /** Catalog breakdown drivers — available before stage detail panel loads. */
  breakdown_drivers?: L2CDriver[];
  panel: L2CPanel | null;
}

export interface L2CTrendPoint {
  period_start: string;
  period_end: string;
  value: number | null;
  prior_value?: number | null;
  status?: L2CStatus;
  stage_status?: L2CStatus;
}

export interface L2CMetricBreakdown {
  stage_id: string;
  metric_key: string;
  driver_id?: string;
  movers: L2CMover[];
  total_count?: number;
  breakdown_unavailable?: boolean;
  error?: string | null;
  drivers?: L2CDriver[];
  from_cache?: boolean;
  /** Plain-language, single-paragraph summary of this metric's current state. */
  metric_summary?: string | null;
  metric_summary_source?: string | null;
}

export interface L2CWeekRange {
  start?: string | null;
  end?: string | null;
}

export interface L2CCapacity {
  talk_hours?: number | null;
  prior_talk_hours?: number | null;
  active_agents?: number | null;
  prior_active_agents?: number | null;
  hours_per_aa_bd?: number | null;
  prior_hours_per_aa_bd?: number | null;
}

export interface L2CComparisonOption {
  preset: L2CComparisonPreset;
  title: string;
  label: string;
  comparison_label?: string | null;
  current_period?: L2CWeekRange | null;
  prior_period?: L2CWeekRange | null;
}

export interface L2CStageDetailRequest {
  grain: L2CPeriodGrain;
  preset: L2CComparisonPreset;
  current_period: L2CWeekRange;
  prior_period: L2CWeekRange;
  headline_metrics: L2CMetric[];
  comparison_label?: string | null;
  comparison_mode?: L2CComparisonMode | null;
  capacity?: L2CCapacity | null;
}

export interface L2CStageDetail {
  stage_id: string;
  panel: L2CPanel | null;
  error?: string | null;
  breakdown_unavailable?: boolean;
  from_cache?: boolean;
  narrative_pending?: boolean;
}

export interface L2CChainSnapshot {
  data_available: boolean;
  message?: string | null;
  period_grain?: L2CPeriodGrain;
  comparison_preset?: L2CComparisonPreset;
  comparison_mode?: L2CComparisonMode;
  comparison_label?: string | null;
  as_of_date?: string | null;
  current_period?: L2CWeekRange | null;
  prior_period?: L2CWeekRange | null;
  current_week?: L2CWeekRange | null;
  prior_week?: L2CWeekRange | null;
  computed_at: string;
  from_cache?: boolean;
  narrative_source?: L2CNarrativeSource;
  chain_summary?: string | null;
  cohort_immature?: boolean | null;
  period_in_progress?: boolean | null;
  capacity?: L2CCapacity | null;
  partial_data?: boolean;
  partial_message?: string | null;
  query_errors?: Record<string, string>;
  stages: L2CStage[];
}
